import type { CreateQuestionRequest } from "@/services/question";
import type { QuestionSettings } from "@/types/question";
import {
	DateRangeType,
	QUESTION_DESCRIPTION_MAX_LENGTH,
	QuestionOptionDisplayType,
	QuestionType,
} from "@/types/question";
import {
	defaultChecklistOptions,
	yesNoOptions,
} from "../constants/questionDefaults";
import type {
	QuestionOption,
	QuestionTypeOrChecklist,
} from "../hooks/useQuestionForm";

type MapToApiFormatParams = {
	title: string;
	selectedType: QuestionTypeOrChecklist | null;
	settings: QuestionSettings;
	options: QuestionOption[];
	description?: string;
};

export const mapToApiFormat = ({
	title,
	selectedType,
	settings,
	options,
	description,
}: MapToApiFormatParams): CreateQuestionRequest | null => {
	if (!(title.trim() && selectedType)) {
		return null;
	}

	const actualType = selectedType === 999 ? QuestionType.Option : selectedType;

	let questionOptions: QuestionOption[] = [];
	if (selectedType === QuestionType.Boolean) {
		questionOptions = yesNoOptions;
	} else if (selectedType === 999) {
		questionOptions = defaultChecklistOptions;
	} else if (selectedType === QuestionType.Option) {
		questionOptions = options;
	}

	const apiOptions = questionOptions.map((opt, index) => ({
		label: opt.text,
		position: index + 1,
		// Only Checklist Padrão (999) gets a default option (first item)
		isDefault: selectedType === 999 && index === 0,
		isDamage:
			opt.text.toLowerCase().includes("avariado") ||
			opt.text.toLowerCase().includes("damage"),
	}));

	const allowsAnswerRecovery = actualType !== QuestionType.File;

	const apiSettings: CreateQuestionRequest["Settings"] = {
		allowAnswerRecovery: allowsAnswerRecovery,
		allowDefaultOption: selectedType === 999,
		allowDamageReport: true,
	};

	if (actualType === QuestionType.Text) {
		if (settings.minLength !== undefined && settings.minLength >= 0) {
			apiSettings.minLength = settings.minLength;
		}
		if (settings.maxLength !== undefined && settings.maxLength > 0) {
			apiSettings.maxLength = settings.maxLength;
		}
	}

	if (actualType === QuestionType.Number) {
		if (settings.minValue !== undefined && settings.minValue >= 0) {
			apiSettings.minValue = settings.minValue;
		}
		if (settings.maxValue !== undefined && settings.maxValue > 0) {
			apiSettings.maxValue = settings.maxValue;
		}
	}

	if (actualType === QuestionType.Date) {
		apiSettings.dateRangeType = settings.dateRangeType ?? DateRangeType.Any;
		if (
			settings.dateRangeType === DateRangeType.Range &&
			settings.minDate &&
			settings.maxDate &&
			settings.minDate > settings.maxDate
		) {
			throw new Error("A data mínima não pode ser maior que a data máxima.");
		}
		if (settings.minDate) {
			apiSettings.minDate = new Date(settings.minDate).toISOString();
		}
		if (settings.maxDate) {
			apiSettings.maxDate = new Date(settings.maxDate).toISOString();
		}
	}

	if (actualType === QuestionType.File) {
		apiSettings.maxFiles = 5;
		apiSettings.maxFileSizeMB = 10;
	}

	if (
		actualType === QuestionType.Option ||
		actualType === QuestionType.Boolean
	) {
		if (selectedType === 999) {
			apiSettings.optionDisplayType = QuestionOptionDisplayType.Icon;
		} else {
			apiSettings.optionDisplayType =
				settings.optionDisplayType ?? QuestionOptionDisplayType.Checkbox;
		}
		apiSettings.isMultipleSelection = settings.isMultipleSelection ?? false;
		if (apiOptions.length > 0) {
			apiSettings.Options = apiOptions;
		}
	}

	const trimmedDescription =
		selectedType === QuestionType.Boolean ? "" : (description?.trim() ?? "");

	return {
		prompt: title.trim(),
		description: trimmedDescription
			? trimmedDescription.slice(0, QUESTION_DESCRIPTION_MAX_LENGTH)
			: null,
		type: actualType,
		Settings: apiSettings,
	};
};
